import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Clock, CheckCircle, Truck, XCircle, MapPin,
  Phone, User, CalendarDays, CreditCard, Zap, Navigation
} from 'lucide-react';
import axios from 'axios';
import { openNavigation } from '../../utils/navigation';
import { orderPayable } from '../../utils/orderAmounts';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { TWO_COLUMNS } from '../../components/customer/customerLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACKING_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: Package },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const STATUS_ORDER = { pending: 0, confirmed: 1, out_for_delivery: 2, delivered: 3, cancelled: -1 };

const CustomerOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { storeSettings } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const currencySymbol = storeSettings?.currency_symbol || '₹';

  useEffect(() => {
    loadOrder();
    // Poll for updates every 30s for active orders
    const interval = setInterval(() => { loadOrder(true); }, 30000);
    return () => clearInterval(interval);
  }, [orderId]);

  const loadOrder = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/orders/${orderId}`);
      setOrder(res.data);
    } catch (error) { if (!silent) toast.error(error.response?.data?.detail || 'Failed to load order'); }
    finally { if (!silent) setLoading(false); }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      // Same as the orders list's cancel form left as it opens: no reason, refund to wallet.
      await axios.post(`${API}/customer/orders/${orderId}/cancel`, { reason: '', refund_to: 'wallet' });
      toast.success('Order cancelled');
      loadOrder();
    } catch (err) { toast.error(apiErrorMessage(err, 'Cannot cancel')); }
  };

  if (loading) {
    return (
      <CustomerLayout width="column" title="Order Details" showBottomNav={false}>
        <div className="p-4 space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      </CustomerLayout>
    );
  }

  if (!order) {
    return (
      <CustomerLayout width="column" title="Order Details" showBottomNav={false}>
        <div className="p-4 text-center py-20">
          <Package className="w-14 h-14 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Order not found</p>
          <Button onClick={() => navigate('/orders')} variant="outline" className="mt-4">Back to Orders</Button>
        </div>
      </CustomerLayout>
    );
  }

  const statusIdx = STATUS_ORDER[order.status] ?? -1;
  const isCancelled = order.status === 'cancelled';
  const isActive = ['confirmed', 'out_for_delivery'].includes(order.status);
  const isSub = order.order_type === 'subscription';

  return (
    <CustomerLayout title="Order Details" showBottomNav={false}>
      <div className="p-4 space-y-4">
        {/* Back */}
        <button onClick={() => navigate('/orders')} className="flex items-center gap-2 text-[var(--theme-primary)] font-medium text-sm" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> Back to Orders
        </button>

        {/* Desktop: the order's progress and delivery on the left, what was ordered
            and the bill on the right. Phones: the same cards in the same order. */}
        <div className={TWO_COLUMNS}>
        <div className="space-y-4 min-w-0" data-testid="order-detail-status-column">
        {/* Order Header */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold text-[var(--theme-text-main)]">#{order.order_number}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isSub ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                {isSub ? 'Subscription' : 'Order'}
              </span>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              isCancelled ? 'bg-red-100 text-red-700' :
              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
              order.status === 'out_for_delivery' ? 'bg-orange-100 text-orange-700' :
              order.status === 'awaiting_payment' ? 'bg-amber-100 text-amber-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {/* awaiting_payment previously fell through to "Placed" — telling the
                  customer an unpaid online order was placed, contradicting the
                  "Payment Pending" shown in the order list. */}
              {isCancelled ? 'Cancelled' : order.status === 'delivered' ? 'Delivered' : order.status === 'out_for_delivery' ? 'Out for Delivery' : order.status === 'confirmed' ? 'Confirmed' : order.status === 'awaiting_payment' ? 'Payment Pending' : 'Placed'}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[var(--theme-text-muted)]">
            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {order.delivery_date}</span>
            {order.delivery_slot_name && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {order.delivery_slot_name}</span>}
            {!order.delivery_slot_name && <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" /> Instant</span>}
          </div>
        </div>

        {/* Tracking Timeline — not for cancelled */}
        {!isCancelled && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-5 border border-black/5" data-testid="order-tracking">
            <h3 className="font-bold text-[var(--theme-text-main)] mb-4">Track Order</h3>
            <div className="space-y-0">
              {TRACKING_STEPS.map((step, idx) => {
                const isCompleted = statusIdx >= idx;
                const isCurrent = statusIdx === idx;
                const StepIcon = step.icon;
                return (
                  <div key={step.key} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        isCompleted ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'
                      } ${isCurrent ? 'ring-2 ring-green-300 ring-offset-2' : ''}`}>
                        <StepIcon className="w-4 h-4" />
                      </div>
                      {idx < TRACKING_STEPS.length - 1 && (
                        <div className={`w-0.5 h-8 ${isCompleted && statusIdx > idx ? 'bg-green-400' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    <div className="pt-1">
                      <p className={`text-sm font-medium ${isCompleted ? 'text-[var(--theme-text-main)]' : 'text-gray-400'}`}>{step.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancelled reason */}
        {isCancelled && order.cancellation_reason && (
          <div className="bg-red-50 rounded-2xl p-4 border border-red-100">
            <p className="text-sm text-red-700"><XCircle className="w-4 h-4 inline mr-1" /> {order.cancellation_reason}</p>
          </div>
        )}

        {/* Delivery Boy Info — show when assigned */}
        {order.delivery_boy_name && isActive && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5" data-testid="delivery-boy-info">
            <h3 className="font-bold text-[var(--theme-text-main)] mb-3">Delivery Partner</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-[var(--theme-text-main)] text-sm">{order.delivery_boy_name}</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">Delivery Partner</p>
                </div>
              </div>
              <div className="flex gap-2">
                {order.delivery_boy_phone && (
                  <a href={`tel:${order.delivery_boy_phone}`}
                    className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors"
                    data-testid="call-delivery-boy">
                    <Phone className="w-5 h-5 text-green-600" />
                  </a>
                )}
              </div>
            </div>

            {/* Track Driver Button — show for confirmed/out_for_delivery */}
            {order.delivery_boy_id && (order.status === 'confirmed' || order.status === 'out_for_delivery') && (
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/delivery/location/${order.delivery_boy_id}`);
                    const loc = res.data;
                    if (loc?.latitude && loc?.longitude) {
                      if (loc.is_store_fallback) {
                        toast.info('Live location not available. Opening store location.');
                      }
                      openNavigation({ latitude: loc.latitude, longitude: loc.longitude });
                    } else {
                      toast.info('Driver location not available yet');
                    }
                  } catch {
                    toast.info('Driver location not available yet. Please try again shortly.');
                  }
                }}
                className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-50 text-blue-700 font-medium text-sm hover:bg-blue-100 transition-colors border border-blue-200"
                data-testid="track-driver-btn"
              >
                <Navigation className="w-4 h-4" /> Track Driver on Map
              </button>
            )}
          </div>
        )}

        {/* Delivery Address */}
        {order.delivery_address && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <h3 className="font-bold text-[var(--theme-text-main)] mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Delivery Address
            </h3>
            <p className="text-sm text-[var(--theme-text-muted)]">
              {typeof order.delivery_address === 'string' ? order.delivery_address :
                order.delivery_address?.full_address || order.delivery_address?.raw || JSON.stringify(order.delivery_address)}
            </p>
          </div>
        )}
        </div>

        <div className="space-y-4 min-w-0" data-testid="order-detail-items-column">
        {/* Items */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
          <h3 className="font-bold text-[var(--theme-text-main)] mb-3">Items ({order.items?.length || 0})</h3>
          <div className="space-y-3">
            {order.items?.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-[var(--theme-text-main)]">{item.product_name}</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">
                    Qty: {item.quantity} × {currencySymbol}{item.price}
                    {item.promotion_name && item.list_price > item.price && (
                      <span className="line-through ml-1">{currencySymbol}{item.list_price}</span>
                    )}
                  </p>
                  {item.promotion_name && (
                    <p className="text-[11px] text-green-700" data-testid={`order-item-promotion-${item.product_id}`}>Promotion: {item.promotion_name}</p>
                  )}
                </div>
                <span className="font-semibold text-sm">{currencySymbol}{item.total?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
          <h3 className="font-bold text-[var(--theme-text-main)] mb-3">Payment</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Subtotal</span>
              <span>{currencySymbol}{order.total_amount?.toFixed(2)}</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between">
                <span className="text-[var(--theme-text-muted)]">Delivery Fee</span>
                <span>{currencySymbol}{order.delivery_fee?.toFixed(2)}</span>
              </div>
            )}
            {order.coupon_discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Coupon Discount</span>
                <span>-{currencySymbol}{order.coupon_discount?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold pt-2 border-t border-black/5 text-base">
              <span>Total</span>
              <span>{currencySymbol}{orderPayable(order).toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <CreditCard className="w-4 h-4 text-[var(--theme-text-muted)]" />
              <span className="text-[var(--theme-text-muted)] capitalize">{order.payment_mode}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Cancel — pending always, confirmed only if store allows it */}
        {(order.status === 'pending' || (order.status === 'confirmed' && storeSettings?.allow_cancel_after_confirm)) && (
          <Button onClick={handleCancelOrder} variant="outline"
            className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-xl h-12" data-testid="cancel-order-btn">
            <XCircle className="w-4 h-4 mr-2" /> Cancel Order
          </Button>
        )}
        </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerOrderDetail;
